/**
 * ECharts 主题：系列色板 + 由 antd token 派生的文字、轴线、分割线、tooltip 配色。
 * 系列色板沿用旧管理中台 composables/useChartConfig.ts 的亮暗两套取值；
 * 其余颜色全部取自当前 antd token，亮暗切换时跟着变。
 */
import type { GlobalToken } from 'antd'
import type { AppliedTheme } from '@/stores/theme'

// 依次为 蓝、绿、黄、红、紫、青
const CHART_PALETTES: Record<AppliedTheme, readonly string[]> = {
  light: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'],
  dark: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']
}

export function getChartPalette(appliedTheme: AppliedTheme): readonly string[] {
  return CHART_PALETTES[appliedTheme]
}

// 坐标轴配色：类目轴和数值轴共用
function axisTheme(token: GlobalToken) {
  return {
    axisLine: { lineStyle: { color: token.colorBorder } },
    axisTick: { lineStyle: { color: token.colorBorder } },
    axisLabel: { color: token.colorTextSecondary },
    splitLine: { lineStyle: { color: token.colorBorderSecondary } }
  }
}

/** 传给 echarts.init 的主题对象 */
export function buildChartTheme(appliedTheme: AppliedTheme, token: GlobalToken) {
  return {
    color: [...getChartPalette(appliedTheme)],
    // 图表铺在 antd Card 上，背景透明即可跟随容器
    backgroundColor: 'transparent',
    textStyle: { color: token.colorText },
    title: { textStyle: { color: token.colorText }, subtextStyle: { color: token.colorTextSecondary } },
    legend: { textStyle: { color: token.colorTextSecondary } },
    tooltip: {
      backgroundColor: token.colorBgElevated,
      borderColor: token.colorBorder,
      textStyle: { color: token.colorText },
      axisPointer: {
        lineStyle: { color: token.colorBorder },
        crossStyle: { color: token.colorBorder },
        label: { backgroundColor: token.colorBgSpotlight, color: token.colorTextLightSolid }
      }
    },
    categoryAxis: axisTheme(token),
    valueAxis: axisTheme(token),
    // 环形图扇区之间的描边与卡片背景同色
    pie: { itemStyle: { borderColor: token.colorBgContainer } }
  }
}
